import { NgClass, NgForOf, NgIf, NgStyle, NgTemplateOutlet } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from "@angular/router";
import { NgClickOutsideDirective, NgClickOutsideExcludeDirective } from 'ng-click-outside2';
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, startWith, Subject, Subscription, switchMap, take, takeUntil, timer } from "rxjs";
import { environment } from "../environments/environment";
import { CentruiLoadingComponent } from "./centrui/src/app/components/centrui-loading/centrui-loading.component";
import {
  CentruiMenuComponent,
  MenuItem,
} from "./centrui/src/app/components/centrui-menu/centrui-menu.component";
import { CentruiOptionComponent } from "./centrui/src/app/components/centrui-option/centrui-option.component";
import { ModalComponent } from "./centrui/src/app/components/modal/modal.component";
import { LogEvent } from "./centrui/src/app/constants/LogEvent";
import { SocketEvent } from "./centrui/src/app/constants/SocketEvent";
import { DarkModeDirective } from "./centrui/src/app/directives/dark-mode.directive";
import { StopClickPropagationDirective } from "./centrui/src/app/directives/stop-click-propagation.directive";
import { User } from "./common/models/User";
import { ApiService } from "./centrui/src/app/services/api.service";
import { AuthService } from "./centrui/src/app/services/auth.service";
import { CustomerService } from "./centrui/src/app/services/customer.service";
import { DropDownService, MenuPosition } from "./centrui/src/app/services/drop-down.service";
import { LoadingService } from "./centrui/src/app/services/loading.service";
import { LogService } from "./centrui/src/app/services/log.service";
import { ModalService } from "./centrui/src/app/services/modal.service";
import { NotifierService } from "./centrui/src/app/services/notifier.service";
import { ImageViewerComponent } from "./common/components/image-viewer/image-viewer.component";
import { Feature } from "./common/constants/Feature";
import { UserRole } from "./common/constants/UserRole";
import { Attachment } from "./common/models/Attachment";
import { BannerNotification } from "./common/models/BannerNotification";
import { Model } from "./common/models/Model";
import { ProjectUserRole } from "./common/models/ProjectUserRole";
import { Release } from "./common/models/Release";
import { AuxService } from "./common/services/aux.service";
import { LightboxService } from "./common/services/lightbox.service";
import { NewVersionAvailableComponent } from "./components/modals/new-version-available/new-version-available.component";
import { LumiComponent } from "./common/components/lumi/lumi.component";
import { NotificationBannerComponent } from "./components/notification-banner/notification-banner.component";
import { IdleService } from "./services/idle.service";
import { SessionActivityService } from "./services/session-activity.service";
import { NotificationBannerService } from "./services/notification-banner.service";
import { PlatformService } from "./services/platform.service";
import { PromptUpdateService } from "./services/prompt-update.service";
import { SocketService } from "./services/socket.service";
import { IntroComponent } from "./centrui/src/app/components/intro/intro.component";
import { IntroService } from "./centrui/src/app/services/intro.service";
import { LicenseService } from "./centrui/src/app/services/license.service";
import { AgencyProfilesComponent } from "./common/components/modals/agency-profiles/agency-profiles.component";
import { ManageAgencyProfilesComponent } from "./common/components/modals/manage-agency-profiles/manage-agency-profiles.component";
import { NgxSmartModalComponent } from "./centrui/src/app/impl/NgxSmartModalComponent";
import { LicenseRole } from "./centrui/src/app/constants/LicenseRole";
import { SocialAuth } from "./centrui/src/app/constants/SocialAuth";
import { FeatureService } from "./common/services/feature.service";
import { SurveyService } from "./common/services/survey.service";
@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    RouterOutlet,
    CentruiOptionComponent,
    NgTemplateOutlet,
    CentruiMenuComponent,
    NgIf,
    NgForOf,
    NgStyle,
    NgClickOutsideDirective,
    NgClickOutsideExcludeDirective,
    DarkModeDirective,
    StopClickPropagationDirective,
    ImageViewerComponent,
    CentruiLoadingComponent,
    NotificationBannerComponent,
    NgClass,
    IntroComponent,
    LumiComponent,
  ],
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  title = "polarixv2-web";
  menuItems: MenuItem[] = [
    {
      title: "Projects",
      routerLink: "projects",
      icon: "new/grid-view.svg",
      darkIcon: "new/grid-view.svg",
    } as MenuItem
  ];
  activeView!: string;
  enabled = false;
  browserSpecificInstructions = "";
  browserZoomAboveBelow = "";
  appLogoSrc = environment.s3 + "/polarix/PolarixIcon.svg";
  appName = "Polarix";
  subs = new Subscription();
  features = Feature;
  menuButton!: ElementRef | null;
  menuStyle!: MenuPosition | null;
  menuLeftSet = true;
  hasActiveReviews = false;
  referenceable: Attachment | undefined;
  loadingMessage: string | undefined;
  notificationBanner!: BannerNotification;
  agencyProfilesModal = false;

  selectedCustomerId: number | undefined;
  destroy$ = new Subject<void>();
  mfaModalOpen = false;
  userFirstLoadComplete = false;

  get agencyManagerProfileRequired(): boolean {
    if (!this.authService.auth()) {
      return false;
    }
    const user = this.authService.userObject as User;
    return !!user && this.licenseService.isAgencyManager(user);
  }

  @ViewChild("menu") menu!: ElementRef;

  constructor(
    public authService: AuthService,
    private router: Router,
    private logService: LogService,
    public platformService: PlatformService,
    private promptUpdateService: PromptUpdateService,
    public dropdownService: DropDownService,
    private changeDetectorRef: ChangeDetectorRef,
    public modalService: ModalService,
    private activatedRoute: ActivatedRoute,
    private auxService: AuxService,
    private viewContainerRef: ViewContainerRef,
    private apiService: ApiService,
    private lightboxService: LightboxService,
    private loadingService: LoadingService,
    public notificationBannerService: NotificationBannerService,
    private idleService: IdleService,
    private sessionActivityService: SessionActivityService,
    private notifierService: NotifierService,
    private customerService: CustomerService,
    private destroyRef: DestroyRef,
    private socketService: SocketService,
    private introService: IntroService,
    private licenseService: LicenseService,
    public featureService: FeatureService,
    private surveyService: SurveyService
  ) {
    // console.log(VERSION);
    this.dropdownService.menuButton.subscribe((menuButton) => {
      this.menuButton = menuButton;
      if (!menuButton) {
        this.menuLeftSet = false;
      }
    });
    this.dropdownService.menuPosition.subscribe((pos) => {
      this.menuStyle = pos;
      if (pos) {
        this.menuLeftSet = false;
      }
    });
    lightboxService.referenceable.subscribe((referenceable) => {
      this.referenceable = referenceable;
    });
    loadingService.loading.subscribe((loadingMessage) => {
      this.loadingMessage = loadingMessage;
    });
    this.auxService.getVersion().subscribe((response: any) => {
      if (response) {
        localStorage.setItem('api-version', response.api_version);
        localStorage.setItem('spa-version', response.spa_version);
        localStorage.setItem('addin-version', response.addin_version);
      }
    });
    this.sessionActivityService.start();
 }

  ngOnInit(): void {
    this.platformService.isFeatureEnabled$(Feature.PROJECT_REVIEW, true, this.destroy$).pipe(distinctUntilChanged()).subscribe(value => {
      if (value) {
        this.getReviewerRoles();
      }
    });
    this.platformService.isFeatureEnabled$(Feature.API_TOKEN_EXPIRATION, true, this.destroy$).subscribe(value => {
      if (value) {
        this.platformService.startTokenExpirationTimer();
      }
    });
    this.platformService.isFeatureEnabled$(Feature.ANALYTICS, true, this.destroy$).subscribe(value => {
      this.loadMenuItems();
    });
    this.authService.userObservable.subscribe((user) => {
      this.subs.unsubscribe();
      if (!user) return;

      if (this.userFirstLoadComplete) {
        this.featureService.refresh(user.customer_id).subscribe(() => {});
      }
      this.userFirstLoadComplete = true;

      const userbackElement = document.getElementById('userback_button_container');
      if (!user) {
        if (userbackElement) {
          userbackElement.style.display = 'none';
        }
        return;
      }
      this.refreshUser(user);

      // const options = {
      //   user_data: {
      //     id: user.id,
      //     info: {
      //       name: user.name,
      //       email: user.email
      //     }
      //   }
      // };
      // Userback(environment.userback, options as UserbackOptions);

      this.subs = new Subscription();
      if (userbackElement) {
        userbackElement.style.display = 'block';
      }

      this.subs.add(
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            const view = event.url.split("/")[1];
            this.activeView = view;
            if (this.authService.auth()) {
              this.logService.log(LogEvent.NAVIGATED, null, event.url);
            }
          }
        }),
      );

      this.subs.add(
        this.socketService.onEvent(SocketEvent.MULTI_DEVICE_LOGIN_ATTEMPT).subscribe(user => {
          this.showAuthedMultiDeviceModal(user);
        }),
      );

      this.subs.add(this.socketService.onEvent(SocketEvent.RELEASE_NOTIFICATION).subscribe((release: Release) => {
        const subdomain = this.getSubdomain();
        if (subdomain != release.client) return;
          timer(0, 5000).pipe(
            switchMap(() =>
              this.auxService.version(subdomain).pipe(
                map(v => v == true),
                catchError(() => of(false))
              )
            ),
            filter(Boolean),
            take(1),
            takeUntil(timer(30_000))
          ).subscribe(() => {
            this.modalService.open(NewVersionAvailableComponent, {}).subscribe();
          });
      }));
    });
    this.notificationBannerService.getBanner();
    this.notificationBannerService.notificationBannerSubject.subscribe((banner) => {
      this.notificationBanner = banner;
    });
    this.customerService.customerId$.subscribe((customerId) => {
      if (!customerId || customerId == this.selectedCustomerId) {
        return;
      }
      if (this.selectedCustomerId !== undefined || customerId != this.authService.userObject?.customer_id) {
        this.featureService.refresh(customerId).subscribe(() => {});
      }
      this.selectedCustomerId = customerId;
    });
    const getSlugFromTree = (node: ActivatedRouteSnapshot | null): string | null => {
      let cur = node;
      let slug: string | null = null;
      let variant: string | null = null;
      let archivedProjectId: string | null = null;
      while (cur) {
        const s = cur.paramMap.get('slug');
        if (s) slug = s;
        const v = cur.paramMap.get('variant');
        if (v) variant = v;
        const id = cur.paramMap.get('id');
        if (id) archivedProjectId = id;
        cur = cur.firstChild ?? null;
      }
      if (variant && !archivedProjectId) {
        return null;
      }
      return slug;
    };

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => getSlugFromTree(this.router.routerState.snapshot.root)),
      startWith(getSlugFromTree(this.router.routerState.snapshot.root)), // initial slug
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(slug => {
      this.platformService.setProjectSlug(slug);
      this.socketService.emitProjectSlugChanged(slug as string);
    });

    this.platformService.projectSlug$.subscribe(slug => {
      // Remove active item id if user is routed away from a project
      if (!slug) {
        sessionStorage.removeItem('activeItemId');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMenuItems() {
    this.menuItems = [
      {
        title: "Projects",
        routerLink: "projects",
        icon: "new/grid-view.svg",
        darkIcon: "new/grid-view.svg",
      } as MenuItem,
      {
        title: "Analytics",
        routerLink: "analytics",
        icon: "new/activity-03.svg",
        darkIcon: "new/activity-03.svg",
        conditionallyShown:
          this.platformService.isFeatureEnabled(Feature.ANALYTICS, true) &&
          this.authService.isAdmin(),
      } as MenuItem,
      {
        title: "Feedback",
        routerLink: "comments",
        icon: "new/comment-01.svg",
        darkIcon: "new/comment-01.svg",
        conditionallyShown: this.authService.isUser(),
      } as MenuItem,
      // {
      //   title: "Review Center",
      //   routerLink: "review-audit-center",
      //   icon: "new/stamp.svg",
      //   darkIcon: "new/stamp.svg",
      //   conditionallyShown:
      //     this.platformService.isFeatureEnabled(Feature.PROJECT_REVIEW, true) &&
      //     this.authService.isUser(),
      //   // conditionallyShown: this.platformService.isFeatureEnabled(Feature.PROJECT_REVIEW) && this.authService.isUser() && this.hasActiveReviews
      // } as MenuItem,
      {
        title: "Settings",
        routerLink: "settings",
        icon: "new/settings-01.svg",
        darkIcon: "new/settings-01.svg",
        conditionallyShown: this.authService.isAdmin(),
      } as MenuItem,
    ];
  }

  private shouldPromptMfaSetup(user: User, hasMfa: boolean): boolean {
    if (!user || !hasMfa || user.mfa_type || this.authService.userObject?.admin || localStorage.getItem('social')) {
      return false;
    }
    return user.license?.role !== LicenseRole.AGENCY || this.licenseService.isAgencyManager(user);
  }

  handleMfa() {
    combineLatest([
      this.authService.userObservable,
      this.featureService.has$(Feature.MFA)
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, hasMfa]) => {
        if (user && !localStorage.getItem('social')) {
          if (this.shouldPromptMfaSetup(user as User, hasMfa) && !this.mfaModalOpen) {
            this.mfaModalOpen = true;
            import('./centrui/src/app/components/modals/mfa-onboarding/mfa-onboarding.component')
              .then(({ MfaOnboardingComponent }) => {
                this.modalService.open(MfaOnboardingComponent, { user: user, disabledEscape: true }).subscribe(() => {
                  this.mfaModalOpen = false;
                  this.scheduleAgencyProfilesModal();
                });
              })
              .catch(() => {
                this.mfaModalOpen = false;
              });
          }
        }
      });
  }

  handleAgencyManagerAuth() {
    combineLatest([
      this.authService.userObservable,
      this.featureService.has$(Feature.MFA)
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([user, hasMfa]) => {
        if (!user || this.mfaModalOpen || !this.canOpenAgencyProfilesModal(user as User, hasMfa)) {
          return;
        }
        this.scheduleAgencyProfilesModal();
      });
  }

  private scheduleAgencyProfilesModal(): void {
    setTimeout(() => this.openAgencyProfilesModal(), 150);
  }

  private canOpenAgencyProfilesModal(user: User, hasMfa: boolean): boolean {
    return this.authService.auth()
      && this.licenseService.isAgencyManager(user)
      && (!hasMfa || !!user.mfa_type);
  }

  private openAgencyProfilesModal(): void {
    const user = this.authService.userObject as User;
    const hasMfa = this.featureService.has(Feature.MFA);
    if (!this.canOpenAgencyProfilesModal(user, hasMfa) || this.agencyProfilesModal || this.mfaModalOpen) {
      return;
    }
    this.agencyProfilesModal = true;

    this.modalService.open(AgencyProfilesComponent, {
      disabledEscape: true,
      manageProfilesHandler: (agencyManager: User) => this.modalService.open(ManageAgencyProfilesComponent, { agencyManager })
    }, {
      closable: false,
      escapable: false,
      dismissable: false,
      backdrop: true
    }).subscribe(() => {
      this.agencyProfilesModal = false;
      if (this.agencyManagerProfileRequired) {
        this.scheduleAgencyProfilesModal();
      }
    });
  }

  ngAfterViewInit(): void {
    this.notifierService.storedNotification();
    this.modalService.setViewContainerRef(this.viewContainerRef);
    this.handleMfa();

    this.handleAgencyManagerAuth();

    document.addEventListener("keydown", (e) => {
      if (
        (e.ctrlKey === true || e.metaKey === true) &&
        (e.which === 61 ||
          e.which === 107 ||
          e.which === 173 ||
          e.which === 109 ||
          e.which === 187 ||
          e.which === 189)
      ) {
        e.preventDefault();
      }
    });

    if (!environment.production) {
      return;
    }
    const browserZoom = this.getZoomLevel();
    const client = this.getBrowser();
    if (client === "safari") {
      this.browserSpecificInstructions =
        '<br/><br/>For <strong>Safari</strong> please click on the view menu from the menu bar and select "Actual Size"';
    } else if (client === "chrome" || client === "edge") {
      this.browserSpecificInstructions =
        '<br/><br/>For <strong>Chrome or Microsoft Edge</strong> please click on the settings menu at the top right of the browser window, denoted by the three vertical dots <span class="settings-menu-style">⋮</span> and use the <strong>+</strong> or <strong>-</strong> buttons to adjust to 100%';
    }

    const data = {
      title: "Before you start…",
      text:
        "There are features within Polarix that require the browser zoom level to be set to <strong>100%</strong>, we have detected that this browser's zoom level is set <strong>" +
        this.browserZoomAboveBelow +
        " 100%</strong> and therefore some of these features will not operate as expected.<br/><br/>Please set your browser's zoom level to <strong>100%</strong> before continuing" +
        this.browserSpecificInstructions,
      confirmButtonText: "Ok got it!",
      style: "primary",
    };

    if (
      browserZoom === "-4" ||
      browserZoom === "-3" ||
      browserZoom === "-2" ||
      browserZoom === "-1"
    ) {
      this.modalService.open(ModalComponent, data).subscribe((result) => {});
    } else if (
      browserZoom === "5" ||
      browserZoom === "4" ||
      browserZoom === "3" ||
      browserZoom === "2" ||
      browserZoom === "1"
    ) {
      this.modalService.open(ModalComponent, data).subscribe((result) => {});
    }
  }

  getSubdomain(hostname = location.hostname) {
    hostname = hostname.split(':')[0];                 // drop port
    if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return '';
    const parts = hostname.toLowerCase().split('.');
    if (parts[0] === 'www') parts.shift();
    return parts.length > 2 ? parts.slice(0, -2).join('.') : '';
  }

  private refreshUser(user: User) {
    if (this.authService.shouldRefreshStoredUser(user)) {
      this.authService.refreshStoredUserFromApi().subscribe();
    }
  }

  getReviewerRoles() {
    this.apiService
      .get(ProjectUserRole, undefined, "own")
      .subscribe((response: Model[]) => {
        this.hasActiveReviews =
          (response as ProjectUserRole[]).filter(
            (userRole) =>
              userRole.role.toString() == UserRole.REVIEWER,
          ).length > 0;
      });
  }

  ngAfterViewChecked(): void {
    if (!this.menuLeftSet && this.menu && this.menuStyle && this.menuButton) {
      this.menuLeftSet = true;
      const menuWidth = this.menu.nativeElement.clientWidth;
      const menuButtonRect = this.menuButton.nativeElement.getBoundingClientRect();
      let menuPositionLeft: number;
      if (menuButtonRect.left + menuWidth > window.innerWidth) {
        menuPositionLeft = Math.floor(
          menuButtonRect.right - menuWidth,
        );
      } else {
        menuPositionLeft = Math.floor(menuButtonRect.left);
      }
      this.menuStyle = { ...this.menuStyle, left: menuPositionLeft + 'px' };
      this.changeDetectorRef.detectChanges();
    }
  }

  clickOutside(): any {
    if (this.dropdownService.shouldIgnoreClickOutside()) {
      return;
    }
    if (document.querySelector('.introjs-tooltip, .introjs-helperLayer, .introjs-overlay')) {
      return;
    }
    this.dropdownService.hideMenu();
  }

  get isGroupChatMenu(): boolean {
    return this.dropdownService.options?.some(option => option.class?.includes('group-chat-option')) ?? false;
  }

  getZoomLevel(): any {
    const screenCssPixelRatio = (window.outerWidth - 8) / window.innerWidth;
    let zoomLevel = "";
    if (screenCssPixelRatio >= 0.46 && screenCssPixelRatio <= 0.54) {
      zoomLevel = "-4";
      this.browserZoomAboveBelow = "below";
    } else if (screenCssPixelRatio <= 0.64) {
      zoomLevel = "-3";
      this.browserZoomAboveBelow = "below";
    } else if (screenCssPixelRatio <= 0.76) {
      zoomLevel = "-2";
      this.browserZoomAboveBelow = "below";
    } else if (screenCssPixelRatio <= 0.92) {
      zoomLevel = "-1";
      this.browserZoomAboveBelow = "below";
    } else if (screenCssPixelRatio <= 1.1) {
      zoomLevel = "0";
    } else if (screenCssPixelRatio <= 1.32) {
      zoomLevel = "1";
      this.browserZoomAboveBelow = "above";
    } else if (screenCssPixelRatio <= 1.58) {
      zoomLevel = "2";
      this.browserZoomAboveBelow = "above";
    } else if (screenCssPixelRatio <= 1.9) {
      zoomLevel = "3";
      this.browserZoomAboveBelow = "above";
    } else if (screenCssPixelRatio <= 2.28) {
      zoomLevel = "4";
      this.browserZoomAboveBelow = "above";
    } else if (screenCssPixelRatio <= 2.7) {
      zoomLevel = "5";
      this.browserZoomAboveBelow = "above";
    } else {
      zoomLevel = "unknown";
      this.browserZoomAboveBelow = "unknown";
    }
    return zoomLevel;
  }

  getBrowser(): any {
    const userAgent = navigator.userAgent;
    let browserName;

    if (userAgent.match(/chrome|chromium|crios/i)) {
      browserName = "chrome";
    } else if (userAgent.match(/firefox|fxios/i)) {
      browserName = "firefox";
    } else if (userAgent.match(/safari/i)) {
      browserName = "safari";
    } else if (userAgent.match(/opr\//i)) {
      browserName = "opera";
    } else if (userAgent.match(/edg/i)) {
      browserName = "edge";
    } else {
      browserName = "No browser detection";
    }

    return browserName;
  }

  checkBannerNotification(): boolean {
    if(!this.notificationBanner) return false;
    if(!this.authService.userObject && (this.notificationBanner.type === 'auth')) {
      return false;
    }
    if(this.authService.userObject && (this.notificationBanner.type === 'no-auth')) {
      return false;
    }
    const bannerNotification = localStorage.getItem('bannerNotification' + this.notificationBanner.id);
    if(!bannerNotification) return true;
    const notification = JSON.parse(bannerNotification);
    if (notification.closed || notification.expiry < Date.now()) {
      return false;
    }
    return true;
  }

  showAuthedMultiDeviceModal(user_id: string) {
    this.modalService.open(ModalComponent, {
      title: 'Maximum Devices',
      text: 'An attempt to log in to this account on another device has been detected. <br><br>Stay logged in on this device?',
      confirmButtonText: 'Stay Logged In'
    }).subscribe((result: NgxSmartModalComponent) => {
      if (result.hasData() && result.getData().data === true) {
        this.socketService.emitMultiDeviceAuthRejected(user_id);
      }
    });
  }
}
